import { z } from "zod";

/**
 * Shared Types for Fawkes API Services
 *
 * These base schemas are used across all service entities
 * to maintain consistency with the Fawkes API patterns.
 */

/**
 * Base entity properties shared by all Fawkes entities
 * Includes: id, audit fields (created/updated by/at)
 * Note: Using z.string() instead of z.string().datetime() for timestamp fields
 * to handle the API's microsecond precision format (e.g., "2025-10-28T15:30:46.809241Z")
 */
export const BaseEntitySchema = z.object({
	id: z.string().uuid(),
	createdBy: z.string().uuid().nullish(),
	createdAt: z.string().nullish(),
	createdAtOffset: z.string().nullish(),
	updatedBy: z.string().uuid().nullish(),
	updatedAt: z.string().nullish(),
	updatedAtOffset: z.string().nullish(),
});

export type BaseEntity = z.infer<typeof BaseEntitySchema>;

/**
 * Extends BaseEntitySchema with backend-supplied audit display name strings.
 * `createdByUser` and `updatedByUser` are plain display name strings sent
 * directly on entity detail responses — no secondary user lookups needed.
 */
export const AuditDisplaySchema = BaseEntitySchema.extend({
	createdByUser: z.string().nullable().optional(),
	updatedByUser: z.string().nullable().optional(),
});

export type AuditDisplay = z.infer<typeof AuditDisplaySchema>;

/**
 * Standard service result format for operations
 * Used by UserInvite and UserSession services
 */
export const ServiceResultSchema = z.object({
	isSuccess: z.boolean(),
	errorMessages: z.array(z.string()).nullable(),
	affectedId: z.string().nullable(),
});

export type ServiceResult = z.infer<typeof ServiceResultSchema>;

/**
 * Status pattern used across entities
 * Contains code, name, and description
 */
export const StatusSchema = z.object({
	code: z.number().int(),
	name: z.string().nullable(),
	description: z.string().nullable(),
});

export type Status = z.infer<typeof StatusSchema>;

/**
 * OrderBy query parameters
 * Used across all paginated endpoints
 */
/**
 * Alternative service result format
 * Some endpoints use 'success' instead of 'isSuccess'
 */
export const SimpleServiceResultSchema = z.object({
	success: z.boolean(),
	affectedId: z.string().nullable(),
});

export type SimpleServiceResult = z.infer<typeof SimpleServiceResultSchema>;

export type OrderByParams = {
	UserId?: string;
	OrganizationUserId?: string;
	StatusId?: number;
	OrganizationId?: string;
	"OrderBy.Fields"?: string[];
	"OrderBy.Descending"?: boolean;
};

/**
 * RFC 7807 Problem Details — matches ProblemDetails2 in swagger
 * Returned by the backend for all 4xx/5xx responses
 */
export const ProblemDetailsSchema = z.object({
	type: z.string().nullish(),
	title: z.string().nullish(),
	status: z.number().int().nullish(),
	detail: z.string().nullish(),
	instance: z.string().nullish(),
});

export type ProblemDetails = z.infer<typeof ProblemDetailsSchema>;
